import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import StarRating from "@/components/StarRating";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

interface OwnerRatingPillProps {
  ownerId: string;
  /** Star size. Defaults to "sm". */
  size?: "xs" | "sm" | "md" | "lg";
  /** Optional class for the wrapper. */
  className?: string;
  /** When true, shows nothing (instead of "No reviews") if owner has none. */
  hideWhenEmpty?: boolean;
}

interface AggregatedRow {
  rating: number;
}

/**
 * Compact pill that shows the average rating + count of revealed owner
 * reviews for a given pet owner. RLS controls which rows are returned:
 * - the owner themselves sees only their own revealed reviews
 * - any vet sees all revealed reviews (cross-vet visibility)
 * - admins see all
 *
 * If there are no visible reviews, renders a muted "No reviews yet" hint
 * unless hideWhenEmpty is set.
 */
const OwnerRatingPill = ({ ownerId, size = "sm", className, hideWhenEmpty }: OwnerRatingPillProps) => {
  const [loading, setLoading] = useState(true);
  const [avg, setAvg] = useState(0);
  const [count, setCount] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      const { data, error } = await supabase
        .from("owner_reviews")
        .select("rating")
        .eq("pet_owner_id", ownerId)
        .not("revealed_at", "is", null);
      if (cancelled) return;
      if (error || !data || data.length === 0) {
        setAvg(0);
        setCount(0);
      } else {
        const rows = data as AggregatedRow[];
        const total = rows.reduce((s, r) => s + (r.rating || 0), 0);
        setAvg(total / rows.length);
        setCount(rows.length);
      }
      setLoading(false);
    };
    if (ownerId) void load();
    return () => { cancelled = true; };
  }, [ownerId]);

  if (loading) {
    return <Skeleton className={cn("h-4 w-20 inline-block align-middle", className)} />;
  }

  if (count === 0) {
    if (hideWhenEmpty) return null;
    return (
      <StarRating
        rating={0}
        size={size}
        showCount={0}
        className={className}
      />
    );
  }

  return (
    <StarRating
      rating={avg}
      size={size}
      showCount={count}
      className={className}
    />
  );
};

export default OwnerRatingPill;
